// frontend/src/pages/admin/lcl/tabs/HelpTab.jsx
import { FiFileText, FiCreditCard, FiClock, FiSettings, FiAlertTriangle, FiCheckCircle, FiDownload, FiInfo } from 'react-icons/fi';

function Section({ icon: Icon, title, color = 'blue', children }) {
  const colorCls = {
    blue:   'text-blue-700 bg-blue-50 border-blue-200',
    green:  'text-green-700 bg-green-50 border-green-200',
    purple: 'text-purple-700 bg-purple-50 border-purple-200',
    orange: 'text-orange-700 bg-orange-50 border-orange-200',
    gray:   'text-gray-700 bg-gray-50 border-gray-200',
  }[color];
  return (
    <div className="bg-white rounded shadow border border-gray-200 overflow-hidden">
      <div className={`flex items-center gap-2 px-3 py-2 border-b ${colorCls}`}>
        <Icon className="h-4 w-4" />
        <div className="text-sm font-bold">{title}</div>
      </div>
      <div className="px-3 py-3 text-[11px] text-gray-700 leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex gap-2">
      <div className="flex-shrink-0 w-4 h-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
        {n}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Badge({ color = 'gray', children }) {
  const cls = {
    gray:   'bg-gray-100 text-gray-700',
    red:    'bg-red-100 text-red-800',
    green:  'bg-green-100 text-green-800',
    blue:   'bg-blue-100 text-blue-800',
    orange: 'bg-orange-100 text-orange-800',
    purple: 'bg-purple-100 text-purple-800',
  }[color];
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${cls}`}>
      {children}
    </span>
  );
}

export default function HelpTab() {
  return (
    <div className="space-y-3">

      {/* Introduction */}
      <Section icon={FiInfo} title="À quoi sert ce module ?" color="blue">
        <p>
          Ce module remplace <strong>intégralement le fichier Excel LCL</strong> (« SOLUTION PRELEVEMENTS SEPA »).
          Il te permet de générer les fichiers à importer dans le portail LCL :
        </p>
        <ul className="list-disc list-inside space-y-0.5 ml-1">
          <li><strong>CSV Mandats</strong> — à importer <em>une seule fois par mandat</em> pour que LCL le connaisse</li>
          <li><strong>CSV Prélèvements</strong> — à générer à chaque vague de prélèvements SEPA périodiques (12 colonnes, format LCL)</li>
        </ul>
        <p>
          Les mandats sont <strong>créés et signés côté CRM</strong>. Ce module lit la base commune et produit
          les fichiers bancaires.
        </p>
      </Section>

      {/* Migration ICS */}
      <Section icon={FiAlertTriangle} title="Migration ICS — ancien vs nouveau créancier" color="orange">
        <div className="space-y-1.5">
          <p>Deux identifiants créancier (ICS) cohabitent :</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-red-50 border border-red-200 rounded p-2">
              <Badge color="red">Ancien ICS</Badge>
              <div className="mt-1 font-mono text-[11px] text-red-800">FR08ZZZ8698E7</div>
              <div className="text-[10px] text-gray-600 mt-0.5">
                Mandats signés <strong>avant le 18/04/2026 08:52</strong>
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-2">
              <Badge color="green">Nouveau ICS</Badge>
              <div className="mt-1 font-mono text-[11px] text-green-800">FR11ZZZ123702</div>
              <div className="text-[10px] text-gray-600 mt-0.5">
                Mandats signés <strong>à partir du 18/04/2026 08:52</strong>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-gray-600 pt-1">
            Règle automatique basée sur la date de signature (le template PDF du mandat a été mis à jour à ce timestamp).
            L'ICS est imprimé en dur dans le PDF signé — impossible de le modifier a posteriori.
          </p>
        </div>
      </Section>

      {/* Onglet Mandats */}
      <Section icon={FiFileText} title="Onglet « Mandats »" color="blue">
        <p><strong>Objectif :</strong> déclarer les mandats SEPA signés au portail LCL (import initial).</p>

        <div className="bg-gray-50 border border-gray-200 rounded p-2 space-y-1.5">
          <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Filtres disponibles</div>
          <ul className="text-[11px] space-y-0.5 ml-1">
            <li>🔍 <strong>Recherche</strong> : nom client, SIREN, RUM</li>
            <li><strong>ICS</strong> : Tous / Nouveau / Ancien</li>
            <li><strong>Statut</strong> : Non exportés / Exportés / Tous</li>
            <li><strong>Période</strong> : Mois précis (ex: 2026-04), Semaine ISO, Année, ou Tout</li>
          </ul>
        </div>

        <div className="pt-1">
          <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Procédure d'export</div>
          <div className="space-y-1.5">
            <Step n={1}>Filtrer les mandats souhaités (ex: nouveau ICS, non exportés, année 2026).</Step>
            <Step n={2}>
              Vérifier les compteurs en haut :
              <Badge color="green">Mandats nouveau ICS</Badge> / <Badge color="red">Mandats ancien ICS</Badge>.
            </Step>
            <Step n={3}>
              Cliquer sur le bouton vert <Badge color="green">CSV Nouveau (X)</Badge> ou rouge <Badge color="red">CSV Ancien (X)</Badge>
              — selon le portail LCL visé.
            </Step>
            <Step n={4}>Confirmer dans la modale. Le fichier CSV se télécharge sur ton poste.</Step>
            <Step n={5}>
              Les mandats exportés disparaissent du compteur (flag <code>lcl_exported_at</code> posé en DB).
              Ils ne peuvent pas être ré-exportés.
            </Step>
            <Step n={6}>Uploader le CSV sur le portail web LCL SOLUTION PRELEVEMENTS SEPA.</Step>
          </div>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-[10px] text-yellow-800">
          ⚠️ <strong>Irréversible</strong> : un mandat ne peut être exporté qu'une seule fois via l'UI.
          En cas de rejet LCL, il faut récupérer le fichier depuis l'onglet Historique ou demander un
          déverrouillage technique.
        </div>
      </Section>

      {/* Onglet Prélèvements */}
      <Section icon={FiCreditCard} title="Onglet « Prélèvements »" color="green">
        <p><strong>Objectif :</strong> générer le fichier CSV LCL pour exécuter une vague de prélèvements SEPA.</p>

        <div className="bg-gray-50 border border-gray-200 rounded p-2 space-y-1">
          <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Pré-requis</div>
          <ul className="text-[11px] space-y-0.5 ml-1 list-disc list-inside">
            <li>Factures au statut <Badge color="blue">ENVOYEE</Badge> avec une <code>date_prelevement</code> renseignée</li>
            <li>Mandat SEPA signé et <strong>déjà déclaré à LCL</strong> via l'onglet Mandats (sinon rejet)</li>
            <li>Compte client avec IBAN + RUM valides</li>
          </ul>
        </div>

        <div className="pt-1">
          <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1">Procédure</div>
          <div className="space-y-1.5">
            <Step n={1}>Choisir la <strong>Date de règlement</strong> (ex: 30/04/2026) — c'est la date d'exécution bancaire.</Step>
            <Step n={2}>
              La liste affiche toutes les factures envoyées avec cette <code>date_prelevement</code> non encore exportées.
            </Step>
            <Step n={3}>Cocher les lignes à inclure dans le lot (toutes par défaut via la case du header).</Step>
            <Step n={4}>
              Vérifier les KPIs : <Badge color="green">Sélectionnés</Badge> et <Badge color="purple">Total €</Badge>.
            </Step>
            <Step n={5}>
              Cliquer <Badge color="green">Générer CSV (X)</Badge> → confirmer → fichier <code>.csv</code> téléchargé.
            </Step>
            <Step n={6}>Uploader le CSV dans le portail LCL. Le flag d'export est posé en DB.</Step>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[10px] text-blue-800">
          💡 <strong>Astuce :</strong> en cas de litige client, décoche la ligne pour l'exclure du lot.
          Tu pourras la ré-inclure dans un export ultérieur.
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded p-2 space-y-1">
          <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">Format du libellé prélèvement</div>
          <p className="text-[11px] text-gray-700">
            <code>FACT&lt;digits-only-num&gt; &lt;CSPE|TICGN&gt;&lt;exercice&gt; &lt;DDMMYY-envoi&gt;</code>
          </p>
          <p className="text-[11px] text-gray-600">
            Exemple : <code>FACT20260015 CSPE2024 310326</code> pour la facture EG-2026-0015 (exercice 2024) envoyée le 31/03/2026.
          </p>
        </div>
      </Section>

      {/* Collisions RUM + date */}
      <Section icon={FiAlertTriangle} title="Collisions RUM + date" color="orange">
        <p>
          LCL refuse 2 prélèvements pour le même mandat (RUM) à la même date. Quand ce cas se produit dans la sélection,
          un modal s'ouvre avant l'export et propose deux résolutions :
        </p>
        <ul className="list-disc list-inside space-y-0.5 ml-1">
          <li>
            <strong>Étalement J+1 ouvré</strong> — la 1ère facture (exercice ASC) garde sa date, la suivante est
            décalée au prochain jour ouvré bancaire FR. La date est mise à jour dans la DB.
          </li>
          <li>
            <strong>Fusion</strong> — les factures du groupe sont sommées en une seule ligne CSV (libellé concaténé).
            Lettrage compta manuel à prévoir.
          </li>
        </ul>
      </Section>

      {/* Correction RIB */}
      <Section icon={FiSettings} title="Correction d'IBAN/BIC" color="blue">
        <p>
          Quand LCL retourne un dossier KO (IBAN/BIC erroné), corriger directement depuis l'onglet Mandats :
        </p>
        <div className="space-y-1.5 pt-1">
          <Step n={1}>
            Cliquer le bouton <Badge color="blue">Corriger RIB</Badge> sur la ligne du mandat concerné.
          </Step>
          <Step n={2}>
            Saisir l'IBAN corrigé (validé en mod 97 côté serveur) et éventuellement le BIC.
          </Step>
          <Step n={3}>
            Sauvegarder — la modification est tracée (qui, quand, ancienne valeur).
          </Step>
        </div>
      </Section>

      {/* Onglet Historique */}
      <Section icon={FiClock} title="Onglet « Historique »" color="purple">
        <p><strong>Objectif :</strong> audit de tous les exports passés + re-téléchargement.</p>
        <ul className="list-disc list-inside space-y-0.5 ml-1">
          <li>Filtres : Type (Mandats CSV / Prélèvements CSV / Prélèvements CFONB legacy), Période précise</li>
          <li>Chaque batch affiche : date d'export, nom du fichier, nb d'items, ICS utilisé</li>
          <li>Bouton <Badge color="gray">Télécharger</Badge> : récupère le fichier original depuis l'archive Supabase Storage</li>
        </ul>
        <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[10px] text-gray-700">
          📦 Tous les fichiers générés sont archivés automatiquement. Utile pour un ré-envoi LCL ou un audit comptable.
        </div>
      </Section>

      {/* Configuration */}
      <Section icon={FiSettings} title="Bouton « Configuration » (header)" color="gray">
        <p>
          Permet d'éditer les paramètres du créancier (ICS, coordonnées bancaires).
          Modifications horodatées et tracées (<code>updated_by</code> + <code>updated_at</code>).
        </p>
        <ul className="list-disc list-inside space-y-0.5 ml-1">
          <li><strong>ICS nouveau / ancien</strong> : si un futur changement arrive, tu peux le saisir ici</li>
          <li><strong>Code Banque / Guichet / Compte / IBAN créancier</strong> : utilisés dans les fichiers générés</li>
          <li><strong>Séquences</strong> (read-only) : numéro incrémenté automatiquement par jour d'export</li>
        </ul>
      </Section>

      {/* Signification des badges */}
      <Section icon={FiCheckCircle} title="Légende des badges et couleurs" color="gray">
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-2">
            <Badge color="green">Nouveau ICS</Badge>
            <span className="text-[10px]">Mandat signé après 18/04/2026</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge color="red">Ancien ICS</Badge>
            <span className="text-[10px]">Mandat signé avant 18/04/2026</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge color="blue">Mandats CSV</Badge>
            <span className="text-[10px]">Batch d'export mandats</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge color="green">Prélèv. CSV</Badge>
            <span className="text-[10px]">Batch d'export prélèvements (nouveau format)</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge color="purple">Prélèv. CFONB</Badge>
            <span className="text-[10px]">Batch d'export prélèvements (legacy CFONB160)</span>
          </div>
        </div>
      </Section>

      {/* Workflow complet */}
      <Section icon={FiDownload} title="Workflow complet en pratique" color="blue">
        <div className="space-y-1.5">
          <Step n={1}>
            <strong>Dans le CRM</strong> : le client signe son mandat SEPA via Yousign.
            La RUM est générée et stockée en DB.
          </Step>
          <Step n={2}>
            <strong>Ici, onglet Mandats</strong> : tu filtres les mandats non exportés,
            tu génères le CSV Nouveau portail, tu l'uploades sur LCL web.
          </Step>
          <Step n={3}>
            <strong>Pendant l'audit fiscal</strong> : tu émets les factures de commission depuis le module Facturation.
            Chaque facture reçoit une <code>date_prelevement = envoi + 60 jours → fin de mois</code>.
          </Step>
          <Step n={4}>
            <strong>À la date de règlement</strong> (ou quelques jours avant) : ici, onglet Prélèvements,
            tu choisis la date, tu coches les factures, tu génères le CSV, tu l'uploades sur LCL.
          </Step>
          <Step n={5}>
            <strong>LCL exécute</strong> les prélèvements. En cas de rejet, tu trouves le fichier original
            dans l'Historique pour investigation.
          </Step>
        </div>
      </Section>

    </div>
  );
}
